// src/App.tsx - Updated with Admin Routes
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Layout from './components/common/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';

// Page imports
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DashboardPage from './pages/dashboard/DashboardPage';
import LeagueListPage from './pages/league/LeagueListPage';
import LeagueDetailPage from './pages/league/LeagueDetailPage';
import CreateLeaguePage from './pages/league/CreateLeaguePage';
import BrowseLeaguesPage from './pages/league/BrowseLeaguesPage';
import PublicLayout from './components/common/PublicLayout';
import PublicTeamPage from './pages/team/PublicTeamPage';
import TeamPage from './pages/team/TeamPage';
import PlayersPage from './pages/players/PlayersPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import DraftPage from './pages/league/DraftPage';
import JoinLeaguePage from './pages/league/JoinLeaguePage';
import MatchupScoreboardPage from './pages/league/MatchupScoreboardPage';
import TradesPage from './pages/league/TradesPage';
import WaiversPage from './pages/league/WaiversPage';
import PlayoffsPage from './pages/league/PlayoffsPage';
import LeagueSettingsPage from './pages/league/LeagueSettingsPage';
import ScoringPage from './pages/scoring/ScoringPage';

// Admin imports
import AdminPage from './pages/admin/AdminPage';
import RosterUpdatePanel from './components/admin/RosterUpdatePanel';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminCleanupPage from './pages/admin/AdminCleanupPage';
import AdminLeaguesPage from './pages/admin/AdminLeaguesPage';
import AdminStatsPage from './pages/admin/AdminStatsPage';

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/join/:code" element={<JoinLeaguePage />} />
          {/* Signed-out surfaces share PublicLayout: these sit outside the
              /app shell, so without it they render with no container at all —
              full-viewport-width text and no way back. */}
          <Route element={<PublicLayout />}>
            <Route path="/browse" element={<BrowseLeaguesPage />} />
            <Route path="/teams/:teamId" element={<PublicTeamPage />} />
          </Route>
          
          {/* Protected routes with layout */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard */}
            <Route index element={<DashboardPage />} />
            
            {/* Leagues */}
            <Route path="leagues" element={<LeagueListPage />} />
            <Route path="leagues/create" element={<CreateLeaguePage />} />
            <Route path="leagues/browse" element={<BrowseLeaguesPage />} />
            <Route path="leagues/:leagueId" element={<LeagueDetailPage />} />
            <Route path="leagues/:leagueId/settings" element={<LeagueSettingsPage />} />
            <Route path="leagues/:leagueId/draft" element={<DraftPage />} />
            <Route path="leagues/:leagueId/matchups/:matchupId" element={<MatchupScoreboardPage />} />
            <Route path="leagues/:leagueId/trades" element={<TradesPage />} />
            <Route path="leagues/:leagueId/waivers" element={<WaiversPage />} />
            <Route path="leagues/:leagueId/playoffs" element={<PlayoffsPage />} />
            
            {/* Teams */}
            <Route path="teams/:teamId" element={<TeamPage />} />
            
            {/* Players */}
            <Route path="players" element={<PlayersPage />} />

            {/* Notifications */}
            <Route path="notifications" element={<NotificationsPage />} />

            {/* Scoring */}
            <Route path="scoring" element={<ScoringPage />} />
            
            {/* Admin Routes */}
            <Route path="admin" element={<AdminPage />} />
            <Route path="admin/roster-updates" element={<RosterUpdatePanel />} />
            <Route path="admin/users" element={<AdminUsersPage />} />
            <Route path="admin/cleanup" element={<AdminCleanupPage />} />
            <Route path="admin/leagues" element={<AdminLeaguesPage />} />
            <Route path="admin/stats" element={<AdminStatsPage />} />
          </Route>
          
          {/* Catch all - redirect to landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;